import {
  CustomFieldConfig,
  DisplayCondition,
} from "@dreambigger/shared/src/types";

/**
 * Extract local dependency field keys from a field's display conditions.
 * Only local conditions (no slug) are relevant for form-level reactivity.
 *
 * Extracted to a standalone util (no JSX) so it can be unit-tested without
 * pulling in the SectionedCustomFieldGrid component's React/antd import chain.
 */
export default function getDisplayDependencyKeys(
  field: CustomFieldConfig,
  allFields?: CustomFieldConfig[],
  visited: ReadonlySet<string> = new Set()
): string[] {
  if (!field.display?.length) return [];
  // Cycle guard: stop traversal if this field is already on the visited chain.
  if (visited.has(field.fieldKey)) return [];
  const nextVisited = new Set(visited);
  nextVisited.add(field.fieldKey);
  const keys = new Set<string>();

  for (const condition of field.display) {
    const conditions = Array.isArray(condition) ? condition : [condition];
    for (const c of conditions) {
      const dc = c as DisplayCondition;
      // Only form-field conditions (no slug, has field) contribute a local
      // dependency key. Conditions gating purely on authenticationMode /
      // utmParam / brandVariantId have no local-field dep — skip them.
      // Mirrors the guard in shouldDisplayField.ts.
      if (!dc.slug && dc.field) {
        keys.add(dc.field.key);
        if (allFields) {
          const dep = allFields.find((f) => f.fieldKey === dc.field?.key);
          if (dep) {
            for (const k of getDisplayDependencyKeys(
              dep,
              allFields,
              nextVisited
            ))
              keys.add(k);
          }
        }
      }
    }
  }
  return Array.from(keys);
}
